import { z } from "zod";
import { geoAreaListSchema } from "./customers.js";
import { defineEndpoint } from "./endpoint.js";
import { publicImageSchema, shopOfferSchema, storefrontHostSchema } from "./storefront.js";
import { shopperSignalsSchema } from "./storefront-tracking.js";
/**
 * Cart and checkout of the online store (B-76, docs/specs/storefront.md "Checkout", SF-07/SF-08). Every call is
 * public and names the shop by host; the storefront server sends the shopper's IP. Prices, delivery charge and
 * totals are always the server's: the browser sends only variants, quantities, the address and choices.
 */
/** Cash on delivery, or an online gateway the shop connected with its own merchant account (B-77). */
export const SHOP_GATEWAYS = ["bkash", "nagad", "sslcommerz"];
export const SHOP_PAYMENT_METHODS = ["cod", ...SHOP_GATEWAYS];
const tag = { tag: "storefront" };
const pub = { public: true };
const host = storefrontHostSchema;
/** The cart's secret (base64url, 43 chars); only its SHA-256 is stored. */
const cartToken = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
const phoneInput = z.string().trim().min(10).max(20);
/** The bot check's answer (Cloudflare Turnstile). */
const captcha = z.string().trim().min(1).max(2048);
export const CART_LINE_PROBLEMS = ["unavailable", "out_of_stock", "not_enough"];
export const cartLineSchema = z.object({
    variantId: z.uuid(),
    productId: z.uuid(),
    slug: z.string(),
    name: z.string(),
    /** `Size: M · Colour: Red`; empty for a product without options. */
    optionLabel: z.string(),
    image: publicImageSchema.nullable(),
    /** Whole units (pieces, kg …). */
    quantity: z.int(),
    unitPricePaisa: z.int(),
    lineTotalPaisa: z.int(),
    /** Why this line cannot be ordered as it is (null: fine). */
    problem: z.enum(CART_LINE_PROBLEMS).nullable(),
    /** DG-07: a digital product (an eBook) — never shipped; an all-digital cart needs no address and no COD. */
    digital: z.boolean().default(false),
});
export const cartCouponSchema = z.object({
    code: z.string(),
    /** What it takes off this cart now (0 with a problem or for free delivery). */
    discountPaisa: z.int(),
    freeDelivery: z.boolean(),
    /** Applied without a code. */
    automatic: z.boolean(),
    problem: z
        .enum(["COUPON_NOT_APPLICABLE", "COUPON_EXPIRED", "COUPON_MIN_ORDER", "COUPON_LIMIT_REACHED"])
        .nullable(),
});
export const cartSchema = z.object({
    lines: z.array(cartLineSchema),
    itemCount: z.int(),
    subtotalPaisa: z.int(),
    /** The code the shopper entered, or an automatic coupon (B-86); null: none. */
    coupon: cartCouponSchema.nullable(),
    /** Every line can be ordered. */
    ready: z.boolean(),
    /** ST-19: the automatic offer this cart is closest to reaching and what is missing; null: none (or a code). */
    nextOffer: shopOfferSchema.extend({ missingPaisa: z.int() }).nullable().default(null),
});
export const createShopCart = defineEndpoint({
    ...tag,
    name: "createShopCart",
    method: "POST",
    path: "/v1/storefront/carts",
    access: pub,
    summary: "A new, empty cart for a shopper (the token is shown once)",
    body: z.object({ host }).strict(),
    status: 201,
    response: z.object({ token: z.string(), cart: cartSchema }),
});
export const viewShopCart = defineEndpoint({
    ...tag,
    name: "viewShopCart",
    method: "POST",
    path: "/v1/storefront/cart",
    access: pub,
    summary: "The cart re-priced now, with lines that can no longer be ordered flagged",
    body: z.object({ host, token: cartToken }).strict(),
    status: 200,
    response: cartSchema,
});
export const MAX_CART_QUANTITY = 99;
export const setShopCartCoupon = defineEndpoint({
    ...tag,
    name: "setShopCartCoupon",
    method: "POST",
    path: "/v1/storefront/cart/coupon",
    access: pub,
    summary: "Apply a coupon code to the cart (null removes it); a code that does not apply says why",
    body: z.object({ host, token: cartToken, code: z.string().trim().max(30).nullable() }).strict(),
    status: 200,
    response: cartSchema,
});
export const setShopCartLine = defineEndpoint({
    ...tag,
    name: "setShopCartLine",
    method: "POST",
    path: "/v1/storefront/cart/lines",
    access: pub,
    summary: "Set how many of a variant are in the cart (0 removes it); `add` adds to what is there",
    body: z
        .object({
        host,
        token: cartToken,
        variantId: z.uuid(),
        quantity: z.int().min(0).max(MAX_CART_QUANTITY),
        add: z.boolean().default(false),
    })
        .strict(),
    status: 200,
    response: cartSchema,
    errors: ["VARIANT_NOT_SELLABLE"],
});
const districtId = z.coerce.number().int().min(1).max(32_767);
export const listShopDistricts = defineEndpoint({
    ...tag,
    name: "listShopDistricts",
    method: "GET",
    path: "/v1/storefront/geo/districts",
    access: pub,
    summary: "Districts for the shopper's address",
    query: z.object({ host }),
    status: 200,
    response: geoAreaListSchema,
});
export const listShopUpazilas = defineEndpoint({
    ...tag,
    name: "listShopUpazilas",
    method: "GET",
    path: "/v1/storefront/geo/upazilas",
    access: pub,
    summary: "Upazilas/thanas of a district for the shopper's address",
    query: z.object({ host, districtId }),
    status: 200,
    response: geoAreaListSchema,
});
export const checkoutQuoteSchema = z.object({
    cart: cartSchema,
    /** Coupon discount (B-86). */
    discountPaisa: z.int(),
    deliveryChargePaisa: z.int(),
    totalPaisa: z.int(),
    codAvailable: z.boolean(),
    /** Gateways the shop has switched on (B-77). */
    gateways: z.array(z.enum(SHOP_GATEWAYS)),
});
export const quoteShopCheckout = defineEndpoint({
    ...tag,
    name: "quoteShopCheckout",
    method: "POST",
    path: "/v1/storefront/checkout/quote",
    access: pub,
    summary: "Delivery charge and total for the cart to an area (an all-digital cart needs no area, DG-07)",
    body: z
        .object({
        host,
        token: cartToken,
        districtId: z.int().min(1).optional(),
        upazilaId: z.int().min(1).nullable().default(null),
    })
        .strict(),
    status: 200,
    response: checkoutQuoteSchema,
});
// Phone OTP (SF-07) -----------------------------------------------------------------------------------------------
export const sendShopOtp = defineEndpoint({
    ...tag,
    name: "sendShopOtp",
    method: "POST",
    path: "/v1/storefront/otp",
    access: pub,
    summary: "Text a 6-digit code to the shopper's phone (bot check first; rate limited per IP, phone and shop)",
    body: z
        .object({
        host,
        phone: phoneInput,
        captcha,
        ip: z.string().max(64).default(""),
        /** DG-01: the SMS wording — an order code at checkout, or an account code (sign-up, reset, verify). */
        purpose: z.enum(["order", "account"]).default("order"),
    })
        .strict(),
    status: 202,
    response: z.object({ expiresInSeconds: z.int(), resendAfterSeconds: z.int() }),
    errors: ["BOT_CHECK_FAILED", "RATE_LIMITED"],
});
export const verifyShopOtp = defineEndpoint({
    ...tag,
    name: "verifyShopOtp",
    method: "POST",
    path: "/v1/storefront/otp/verify",
    access: pub,
    summary: "Check the code; a ticket proves the phone for 30 minutes",
    body: z.object({ host, phone: phoneInput, code: z.string().regex(/^\d{6}$/) }).strict(),
    status: 200,
    response: z.object({ ticket: z.string(), expiresAt: z.iso.datetime() }),
    errors: ["INVALID_OTP", "RATE_LIMITED"],
});
// Placing the order (SF-08) -----------------------------------------------------------------------------------------
export const placeShopOrderSchema = z
    .object({
    host,
    token: cartToken,
    /** From the checkout page: the same ID again returns the same order (double taps, retries). */
    checkoutId: z.uuid(),
    /** From `verifyShopOtp`; needed only while the shop asks for an SMS code at checkout. */
    ticket: z.string().min(1).max(100).optional(),
    /** DG-01: the shopper's session when signed in; the order then belongs to the account (ACC-06). */
    session: z
        .string()
        .regex(/^[A-Za-z0-9_-]{43}$/)
        .optional(),
    captcha,
    ip: z.string().max(64).default(""),
    name: z.string().trim().min(1).max(200),
    phone: phoneInput,
    /** DG-06: not needed when every cart line is digital (nothing is shipped). */
    districtId: z.int().min(1).optional(),
    upazilaId: z.int().min(1).nullable().default(null),
    addressLine: z.string().trim().min(3).max(300).optional(),
    note: z.string().trim().max(500).default(""),
    paymentMethod: z.enum(SHOP_PAYMENT_METHODS),
    /** B-82: Meta cookies and user agent for the Conversions API. */
    signals: shopperSignalsSchema.optional(),
})
    .strict();
export const placedShopOrderSchema = z.object({
    orderId: z.uuid(),
    number: z.string(),
    totalPaisa: z.int(),
    deliveryChargePaisa: z.int(),
    paymentMethod: z.enum(SHOP_PAYMENT_METHODS),
    /** Online payment: where to send the shopper to pay (null for cash on delivery). */
    payment: z.object({ sessionId: z.uuid(), redirectUrl: z.url() }).nullable(),
});
export const placeShopOrder = defineEndpoint({
    ...tag,
    name: "placeShopOrder",
    method: "POST",
    path: "/v1/storefront/orders",
    access: pub,
    summary: "Place the cart as an order (stock reserved at once; the same checkoutId returns the same order)",
    body: placeShopOrderSchema,
    status: 201,
    response: placedShopOrderSchema,
    errors: [
        "INVALID_OTP",
        "BOT_CHECK_FAILED",
        "INSUFFICIENT_STOCK",
        "VARIANT_NOT_SELLABLE",
        "BUSINESS_RULE_VIOLATION",
    ],
});
//# sourceMappingURL=storefront-checkout.js.map